import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container, Typography, Paper, Grid, CircularProgress, Box, Button, TextField, Snackbar, IconButton,
    Breadcrumbs
} from '@mui/material';
import { styled } from '@mui/system';
import CloseIcon from '@mui/icons-material/Close';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { createListing, uploadListingImages, getTopLevelCategories, getSubcategories, getCategoryPath } from '../services/api';
import CategorySelector from '../components/CategorySelector';
import LocationChips from '../components/LocationChips';
import MapComponent from '../components/MapComponent';
import InputAdornment from '@mui/material/InputAdornment';


const StyledContainer = styled(Container)(({ theme }) => ({
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
}));

const StyledButton = styled(Button)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    borderRadius: '16px',
    backgroundColor: 'transparent',
    color: theme.palette.primary.main,
    padding: theme.spacing(1.5, 3),
    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
        boxShadow: '0px 6px 8px rgba(0, 0, 0, 0.15)',
    },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[3],
}));

const ImagePreview = styled('img')({
    width: '100px',
    height: '100px',
    objectFit: 'cover',
    margin: '5px',
    borderRadius: '4px',
});

const CategoryLogo = styled('img')({
    width: '48px',
    height: '48px',
    objectFit: 'contain',
    marginRight: '16px',
});

const CategoryBreadcrumb = styled(Breadcrumbs)({
    marginBottom: '16px',
    '& .MuiBreadcrumbs-separator': {
        margin: '0 8px',
    },
});

const BreadcrumbText = styled(Typography)({
    fontSize: '0.875rem',
    color: 'text.secondary',
    '&:last-child': {
        color: 'text.primary',
        fontWeight: 500,
    },
});

const ImagePreviewContainer = styled(Box)({
    position: 'relative',
    display: 'inline-block',
});

const DeleteButton = styled(IconButton)({
    position: 'absolute',
    top: 0,
    right: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
    },
});

function CreateListing() {
    const navigate = useNavigate();
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [price, setPrice] = useState('');
    const [priceError, setPriceError] = useState(false);
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '' });
    const [categoryDialogOpen, setCategoryDialogOpen] = useState(true);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [categoryPath, setCategoryPath] = useState([]);
    const [locations, setLocations] = useState([]);
    const [fullCategoryPath, setFullCategoryPath] = useState([]);

    useEffect(() => {
        fetchTopLevelCategories();
    }, []);

    const fetchTopLevelCategories = async () => {
        try {
            const response = await getTopLevelCategories();
            setCategories(response.data);
        } catch (error) {
            console.error('Error fetching top-level categories:', error);
            setSnackbar({ open: true, message: 'Failed to load categories. Please try again.' });
        }
    };

    const fetchCategoryPath = async (categoryId) => {
        try {
            const response = await getCategoryPath(categoryId);
            setFullCategoryPath(response.data);
        } catch (error) {
            console.error('Error fetching category path:', error);
        }
    };

    const handleCategorySelect = async (category) => {
        try {
            const response = await getSubcategories(category.id);
            if (response.data.length > 0) {
                setCategories(response.data);
                setCategoryPath([...categoryPath, category]);
            } else {
                setSelectedCategory(category);
                setCategoryDialogOpen(false);
                await fetchCategoryPath(category.id);
            }
        } catch (error) {
            console.error('Error fetching subcategories:', error);
            setSnackbar({ open: true, message: 'Failed to load subcategories. Please try again.' });
        }
    };

    const handleCategoryBack = async () => {
        if (categoryPath.length > 1) {
            const newPath = [...categoryPath];
            newPath.pop();
            setCategoryPath(newPath);
            const parentCategory = newPath[newPath.length - 1];
            const response = await getSubcategories(parentCategory.id);
            setCategories(response.data);
        } else if (categoryPath.length === 1) {
            setCategoryPath([]);
            fetchTopLevelCategories();
        }
    };

    const handleLocationChange = (newLocation) => {
        if (locations.length < 3) {
            setLocations([...locations, { latitude: newLocation.lat, longitude: newLocation.lng }]);
        }
    };

    const handleLocationDelete = (index) => {
        setLocations(locations.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedCategory) {
            setSnackbar({ open: true, message: 'Please select a category before submitting.' });
            return;
        }
        setLoading(true);

        try {
            const listingData = {
                title,
                description,
                price: parseFloat(price),
                category_id: selectedCategory.id,
                locations: locations,
            };
            const response = await createListing(listingData);
            const newListingId = response.data.id;

            if (images.length > 0) {
                const formData = new FormData();
                images.forEach((file) => formData.append('images', file));
                await uploadListingImages(newListingId, formData);
            }

            setSnackbar({ open: true, message: 'Listing created successfully!' });
            setTimeout(() => navigate(`/my-listings/${newListingId}`), 2000);
        } catch (error) {
            console.error('Error creating listing:', error);
            setSnackbar({ open: true, message: 'Failed to create listing. Please try again.' });
        } finally {
            setLoading(false);
        }
    };

    const handleImageChange = (e) => {
        const newImages = Array.from(e.target.files);
        setImages([...images, ...newImages]);
    };

    const handleImageDelete = (index) => {
        setImages(images.filter((_, i) => i !== index));
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar({ ...snackbar, open: false });
    };

    return (
        <StyledContainer maxWidth="md">
            <StyledPaper elevation={3}>
                <Typography variant="h4" gutterBottom>
                    Create New Listing
                </Typography>
                {fullCategoryPath.length > 0 && (
                    <CategoryBreadcrumb separator={<NavigateNextIcon fontSize="small" />}>
                        {fullCategoryPath.map((cat, index) => (
                            <BreadcrumbText key={cat.id}>
                                {cat.name}
                            </BreadcrumbText>
                        ))}
                    </CategoryBreadcrumb>
                )}
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Category"
                                value={selectedCategory?.name || ''}
                                onClick={() => {
                                    setCategoryDialogOpen(true);
                                    if (categoryPath.length === 0) fetchTopLevelCategories();
                                }}
                                InputProps={{
                                    startAdornment: selectedCategory && (
                                        <CategoryLogo
                                            src={`${process.env.PUBLIC_URL}/icons/${selectedCategory.id}.png`}
                                            alt={selectedCategory.name}
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = `${process.env.PUBLIC_URL}/icons/X.png`;
                                            }}
                                        />
                                    ),
                                    readOnly: true
                                }}
                                margin="normal"
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Title"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                margin="normal"
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label="Description"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                margin="normal"
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                type="text"              // allow the “$” to stick
                                label="Price per day"
                                value={price}
                                onChange={(e) => {
                                    const inputVal = e.target.value.replace(/[^0-9.]/g, '');
                                    setPrice(inputVal);
                                    setPriceError(!inputVal || parseFloat(inputVal) < 5);
                                }}
                                error={priceError}
                                helperText={priceError ? "Price must be at least $5 USD" : "Enter amount in USD (minimum $5)"}
                                margin="normal"
                                required
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                    inputProps: { min: 5, step: 0.01 }
                                }}
                            />

                        </Grid>
                        <Grid item xs={12}>
                            <input
                                accept="image/*"
                                style={{ display: 'none' }}
                                id="upload-button-file"
                                multiple
                                type="file"
                                onChange={handleImageChange}
                            />
                            <label htmlFor="upload-button-file">
                                <StyledButton variant="contained" component="span" fullWidth sx={{ mt: 2 }}>
                                    Upload Images
                                </StyledButton>
                            </label>
                            <Box mt={2}>
                                {images.map((image, index) => (
                                    <ImagePreviewContainer key={index}>
                                        <ImagePreview src={URL.createObjectURL(image)} alt={`Preview ${index + 1}`} />
                                        <DeleteButton size="small" onClick={() => handleImageDelete(index)}>
                                            <CloseIcon fontSize="small" />
                                        </DeleteButton>
                                    </ImagePreviewContainer>
                                ))}
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" gutterBottom>
                                Item Locations (up to 3)
                            </Typography>
                            <MapComponent
                                center={locations[0] ? [locations[0].latitude, locations[0].longitude] : [40.7128, -74.0060]}
                                locations={locations}
                                onLocationChange={handleLocationChange}
                            />
                            <LocationChips
                                locations={locations}
                                handleDelete={handleLocationDelete}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <StyledButton
                                type="submit"
                                variant="contained"
                                color="primary"
                                fullWidth
                                disabled={loading || !selectedCategory}
                                sx={{ mt: 4 }}
                            >
                                {loading ? <CircularProgress size={24} /> : 'Create Listing'}
                            </StyledButton>
                        </Grid>
                    </Grid>
                </form>
            </StyledPaper>

            <CategorySelector
                open={categoryDialogOpen}
                handleClose={() => setCategoryDialogOpen(false)}
                categories={categories}
                handleCategorySelect={handleCategorySelect}
                handleCategoryBack={handleCategoryBack}
                hasBackButton={categoryPath.length > 0}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                message={snackbar.message}
            />
        </StyledContainer>
    );
}

export default CreateListing;
